import { Mutation } from "@apollo/client/react/components";
import { RawDraftContentState } from "draft-js";
import pick from "lodash/fp/pick";
import React, { ReactElement } from "react";
import ReplyCommentMutation from "~/lib/mutations/ReplyComment";
import {
  IReplyCommentMutation,
  IReplyCommentMutationVariables,
} from "~/lib/mutations/__generated__/ReplyComment.generated";
import CommentsQuery from "~/lib/queries/CommentsQuery";
import optimisticId from "~/lib/utils/optimisticId";
import { ICommentTargetType, IUser } from "~/types/types";
import Auth from "~/components/auth/Auth";
import convertUserTypenameToAuthor from "~/components/comment/convertUserTypenameToAuthor";
import { ICommentFieldsLegacyFragment } from "~/lib/queries/fragments/__generated__/CommentFields.generated";

interface IReplyCommentProps {
  targetId: string;
  targetType: ICommentTargetType;
  children: (replyComment: (replyTo: string, body: RawDraftContentState) => void) => ReactElement | null;
}

export default function ReplyComment({ targetId, targetType, children, ...props }: IReplyCommentProps): ReactElement {
  return (
    <Mutation<IReplyCommentMutation, IReplyCommentMutationVariables> mutation={ReplyCommentMutation} {...props}>
      {(replyComment) => {
        return (
          <Auth>
            {({ user }) => {
              return children((replyTo: string, body: RawDraftContentState) => {
                const createdAt = new Date().toISOString();

                return replyComment({
                  update: (proxy, { data }) => {
                    const queryResult = proxy.readQuery({
                      query: CommentsQuery,
                      variables: {
                        targetId,
                        targetType,
                        cursor: null,
                      },
                    });

                    if (!queryResult) {
                      return;
                    }

                    const { comments } = queryResult as any;
                    const { replyComment } = data as any;

                    proxy.writeQuery({
                      query: CommentsQuery,
                      variables: {
                        targetId,
                        targetType,
                        cursor: null,
                      },
                      data: {
                        comments: {
                          ...comments,
                          items: comments.items.map((comment: ICommentFieldsLegacyFragment) => {
                            if (comment._id === replyTo) {
                              return {
                                ...comment,
                                replies: ((comment.replies as any) || []).concat({
                                  ...replyComment,
                                  author: {
                                    ...pickCommentAuthorFields(replyComment.author),
                                    __typename: convertUserTypenameToAuthor(replyComment.author),
                                  },
                                }),
                              };
                            } else {
                              return comment;
                            }
                          }),
                        },
                      },
                    });
                  },
                  variables: {
                    replyTo,
                    body: body as any,
                  },
                  optimisticResponse: {
                    __typename: "Mutation",
                    replyComment: {
                      __typename: "Comment" as any,
                      _id: optimisticId(),
                      body: body as any,
                      upvotesCount: 0,
                      viewerDidUpvote: false,
                      createdAt: createdAt as any,
                      lastUpdatedAt: null,
                      isReply: false,
                      author: {
                        ...(pickCommentAuthorFields(user) as any),
                        __typename: convertUserTypenameToAuthor(user as IUser) as any,
                      },
                      replies: [],
                    },
                  },
                });
              });
            }}
          </Auth>
        );
      }}
    </Mutation>
  );
}

const pickCommentAuthorFields = pick(["_id", "slug", "firstName", "fullName", "jobTitle", "avatar", "isInnovator"]);
